import React, { useCallback, useMemo } from 'react'
import { FaBarcode } from 'react-icons/fa'
import {
  FiCheck,
  FiFolder,
  FiHash,
  FiPlus,
  FiTag,
  FiTrash,
  FiX
} from 'react-icons/fi'
import { IoSparklesSharp } from 'react-icons/io5'
import { LuListTree } from 'react-icons/lu'
import { useDispatch } from 'react-redux'
import { Alert, IconStorefront } from '@vtex/shoreline'
import { t } from 'i18next'

import Svg from '~/components/svg'
import ThumbZoomable from '~/components/ThumbZoomable'
import Tooltip from '~/components/Tooltip'
import { Button } from '~/components/Buttons'
import { Badge } from '~/components/UI/Badge'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'
import { addProductOnCampaign } from '~/modules/retailMedia/store/campaign/actions'
import { DEFAULT_SELECTED_SUBPUBLISHER_CATALOG } from '~/modules/retailMedia/constants/subpublisherCatalogs'

import modelProduct from './model'

import * as S from './styles'

/**
 * Componente responsável por renderizar um produto nas listagens.
 * @param {Object} data Dados do produto
 * @param {boolean} isSelected Informa se o produto está na lista dos selecionados
 * @param {string[]} selectedSKUS Informa os SKUS selecionados para mudarmos o botão no painel de busca
 * @param {boolean} hasAction Informa se o produto está numa página de edição e deve exibir botões de ação
 */
const ProductContent: React.FC<ProductProps> = ({
  data,
  isSelected = false,
  selectedSKUS = null,
  selectedIDs = null,
  hasAction = false,
  itemsDisableds = null,
  shouldConfirmDelete = false,
  setIsModalOpen,
  handleDeleteProduct,
  subPublisherIds,
  allSubpublishers,
  campaignType,
  ...rest
}) => {
  const dispatch = useDispatch()
  const { tCapitalize } = useAPPTranslation()

  const product = modelProduct(data)
  const productGtins = useMemo(
    () =>
      (product?.gtin || [])
        .map(item => item?.trim?.() || item)
        .filter(Boolean),
    [product?.gtin]
  )

  const isSelectedOnSearch =
    selectedSKUS?.find(item => item === data.product_sku) ||
    selectedIDs?.find(item => item === data.id)
  const shouldShowCompactEans = useMemo(
    () => (isSelected || hasAction) && productGtins.length > 1,
    [isSelected, hasAction, productGtins.length]
  )
  const gtinBadges = useMemo(() => {
    if (!productGtins.length) return []

    if (shouldShowCompactEans) {
      return [
        {
          key: 'product-gtins-count',
          label: t('rm:ProductsPanel.GtinCount', { count: productGtins.length }),
          tooltipText: (
            <div className="d-flex flex-column gap-1">
              {productGtins.map((item, index) => (
                <span key={`gtin_detail_${item}_${index}`}>{item}</span>
              ))}
            </div>
          )
        }
      ]
    }

    return productGtins.map((item, index) => ({
      key: `product-gtin-${item}-${index}`,
      label: item,
      tooltipText: 'GTIN'
    }))
  }, [productGtins, shouldShowCompactEans])

  const handleAddProduct = useCallback(
    () => dispatch(addProductOnCampaign({ product: data, subPublisherIds })),
    [data, dispatch, subPublisherIds]
  )

  const handleDeleteProductConfirm = () => {
    setIsModalOpen(true)
  }

  const actionOnDelete = shouldConfirmDelete
    ? handleDeleteProductConfirm
    : handleDeleteProduct

  const isDisabled = itemsDisableds?.find(
    item => item.product_sku === data.product_sku || item.id === data.id
  )

  const catalogBadge = useMemo(
    () =>
      !!allSubpublishers &&
      product?.publisher_id &&
      allSubpublishers?.find(item => item?.id === product?.publisher_id)
        ? {
            label: allSubpublishers?.find(
              item => item?.id === product?.publisher_id
            )?.name,
            icon: <IconStorefront width={12} />
          }
        : {
            label: DEFAULT_SELECTED_SUBPUBLISHER_CATALOG.label,
            icon: <Svg src="icons/graph_5" width="auto" height="12" />
          },
    [allSubpublishers, product?.publisher_id]
  )

  return (
    <S.Container {...rest}>
        {!!product?.image && (
          <S.Image className={!!data.item_disabled && 'disabled'}>
            <ThumbZoomable
              size="tiny"
              url={product.image}
              title={product.name}
            />
          </S.Image>
        )}

        <S.Infos className={`d-flex flex-column gap-1${data.item_disabled ? ' disabled' : ''}`}>
          {product.isSuggested && (
            <Badge
              template="primary"
              iconLeft={<IoSparklesSharp />}
              className="mb-1"
              text={t('rm:objectiveBased.AutomaticallySelectedProducts')}
              size="tiny"
            />
          )}

          {!!product?.categories?.length && (
            <div className="secondary gap-1">
              <S.InfoItem>
                <FiFolder /> {product?.categories?.at(-1)}
              </S.InfoItem>
            </div>
          )}

          {product.name && (
            <div className="mainInfo">
              <div className="fw-bold fs-6">{product.name}</div>
            </div>
          )}

          <footer className="secondary gap-1">
            {gtinBadges?.map(item => (
              <Tooltip
                noMargin
                key={item?.key}
                text={item?.tooltipText}
                icon={
                  <Badge
                    size="tiny"
                    iconLeft={<FaBarcode />}
                    className="m-0"
                    text={item?.label}
                  />
                }
              />
            ))}

            {product.product_sku ? (
              <Tooltip
                noMargin
                text="SKU"
                icon={
                  <Badge
                    size="tiny"
                    iconLeft={<FiTag />}
                    className="m-0"
                    text={product.product_sku}
                  />
                }
              />
            ) : (
              <Alert variant="warning">
                {t('rm:ProductsPanel.skuNotFound')}
              </Alert>
            )}

            {campaignType === 'off_site' && (
              <Badge
                size="tiny"
                iconLeft={catalogBadge.icon}
                className="m-0"
                text={catalogBadge.label}
              />
            )}

            {product?.productReference && (
              <Tooltip
                noMargin
                text={t('rm:ProductsPanel.ProductReferenceCodes')}
                icon={
                  <Badge
                    size="tiny"
                    iconLeft={<LuListTree />}
                    className="m-0"
                    text={product.productReference}
                  />
                }
              />
            )}

            {product?.reference?.map(item => (
              <Tooltip
                noMargin
                key={`ref_${item}`}
                text={t('rm:ProductsPanel.AdditionalReferences')}
                icon={
                  <Badge
                    size="tiny"
                    iconLeft={<FiHash />}
                    className="m-0"
                    text={item}
                  />
                }
              />
            ))}
          </footer>
        </S.Infos>

        {hasAction && (
          <S.Action>
            {isSelectedOnSearch ? (
              <S.SelectedOnSearch className={!!isDisabled && 'not-hover'}>
                <Button
                  template="primary"
                  text={tCapitalize('c:words.selected')}
                  iconLeft={<FiCheck />}
                  className="selectedItem"
                  size="xsmall"
                  customWidth="auto"
                />

                <Button
                  template="transparentCritical"
                  text={tCapitalize('c:actions.remove')}
                  iconLeft={<FiX />}
                  onClick={actionOnDelete}
                  className="removeItem"
                  size="xsmall"
                  customWidth="auto"
                  disabled={!!isDisabled}
                  name="removeItem"
                />
              </S.SelectedOnSearch>
            ) : (
              <>
                {isSelected ? (
                  data.item_disabled ? (
                    <Tooltip
                      text={t('rm:ProductsPanel.alert.forbiddenRemoveProduct')}
                      icon={
                        <Button
                          template="secondary"
                          text={<FiCheck className="checkIcon" />}
                          size="xsmall"
                          customWidth="auto"
                          disabled={!!data.item_disabled}
                          className="buttonToolTipChecked disabled"
                          aria-label="Selected item"
                        />
                      }
                    />
                  ) : (
                    <Button
                      template="transparentCritical"
                      text={<FiTrash />}
                      onClick={actionOnDelete}
                      size="xsmall"
                      customWidth="auto"
                      aria-label="Remove item"
                    />
                  )
                ) : (
                  <Button
                    template="secondary"
                    text={t('c:actions.select')}
                    iconLeft={<FiPlus />}
                    onClick={handleAddProduct}
                    size="xsmall"
                    customWidth="auto"
                  />
                )}
              </>
            )}
          </S.Action>
        )}
    </S.Container>
  )
}

export default ProductContent
